/* -----------------Globals--------------- */
import { useCallback, useEffect, useMemo, useState } from "react";

import type { SessionConfigFiltersType } from "@configs/custom-app-config-schema";
import { calculateTagDist, isItemArray } from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { Distribution } from "@memorang/ui/src/types";
import uniqBy from "lodash/uniqBy";
import type { TagsByType } from "types/tag";
/* -----------------Helpers & Hooks--------------- */
import {
	calculateCheckboxStatusAndAvailableItems,
	filterTagsByTagIds,
	getFinalTagIds,
	getFinalTagsDistForMode,
	getNewDistributionCheckboxstatesAndCount,
	getOptimalValue,
	getTagIds,
	initializeCheckboxStates,
	onCheckBoxStatusUpdated,
} from "../helpers/filter";
/* -----------------Types--------------- */
import { LearningModes } from "../types";
import type { CheckboxState } from "../types/filter";
import type { SectionTag } from "../types/tag";

export type AdditionalParams = {
	tagsByType?: TagsByType;
	studyRecommenedTopics?: boolean;
	cstDistribution?: Distribution;
	hasAccess?: boolean;
	freeTagIds?: number[];
	selectedTagIds?: number[];
	sectionTags?: SectionTag[];
	diagnosticSectionTags?: SectionTag[];
	numItemsMap?: Record<string, number>;
};

const useSessionConfig = ({
	availableDistribution,
	additionalParams,
	isSessionCreationInProgress,
	showLearningMode,
	showDistributionFilters,
	distributionFilterItems,
}: {
	availableDistribution: Distribution;
	additionalParams: AdditionalParams;
	isSessionCreationInProgress?: boolean;
	showLearningMode?: boolean;
	showDistributionFilters?: boolean;
	distributionFilterItems: SessionConfigFiltersType;
}) => {
	const { tagsByType, hasAccess, freeTagIds, selectedTagIds } =
		additionalParams;
	const [selectedLearningMode, setSelectedLearningMode] =
		useState<LearningModes>(LearningModes.questions);

	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider ?? false,
	);
	const showPointsBasedScore = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);
	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);

	const [selectedTagCheckboxMap, setSelectedTagCheckboxMap] =
		useState<CheckboxState>();

	const [finalAvailableDist, setFinalAvailableDist] = useState(
		availableDistribution,
	);

	const distributionKeys = Object.keys(availableDistribution);
	const numAvailableItems = distributionKeys.reduce((a, c) => {
		return a + availableDistribution[c as keyof Distribution];
	}, 0);

	const optimalDefaultRequiredItems = showItemsSlider
		? numAvailableItems > 50
			? 20
			: getOptimalValue(numAvailableItems, 10)
		: numAvailableItems;

	const [requiredDistribution, setRequiredDistribution] = useState(
		availableDistribution,
	);

	const [numRequiredItems, setNumRequiredItems] = useState(
		optimalDefaultRequiredItems,
	);

	const [numTotalItemsFiltered, setNumTotalItemsFiltered] = useState(0);

	const [availableItems, setAvailableItems] = useState(numAvailableItems);

	const [selectedDistributionCheckboxMap, setSelectedDistributionCheckboxMap] =
		useState<CheckboxState>(
			initializeCheckboxStates(
				numRequiredItems,
				finalAvailableDist,
				showItemsSlider,
			),
		);

	useEffect(() => {
		if (selectedDistributionCheckboxMap) {
			const checkedItems = Object.keys(selectedDistributionCheckboxMap).filter(
				(item) => selectedDistributionCheckboxMap[item],
			);

			const count = checkedItems.reduce((a, c) => {
				let acc = a;
				acc += finalAvailableDist[c as keyof Distribution];
				return acc;
			}, 0);
			setNumTotalItemsFiltered(count);
		}
	}, [finalAvailableDist, selectedDistributionCheckboxMap]);
	const allTags = useMemo(() => {
		return tagsByType ? Object.values(tagsByType).flat() : [];
	}, [tagsByType]);

	const handleSetLearningMode = (mode: LearningModes) => {
		setSelectedLearningMode(mode);
	};

	const handleDistributionCheckboxState = useCallback(
		(checkboxState: CheckboxState) => {
			const { requiredDistribution: rq, questionCount } =
				onCheckBoxStatusUpdated(
					finalAvailableDist,
					checkboxState,
					numRequiredItems || optimalDefaultRequiredItems,
					distributionFilterItems,
				);

			setAvailableItems(questionCount);
			setRequiredDistribution(rq);
			setSelectedDistributionCheckboxMap(checkboxState);
			setNumRequiredItems(
				showItemsSlider ? Math.min(questionCount, 10) : questionCount,
			);
		},
		[
			finalAvailableDist,
			numRequiredItems,
			optimalDefaultRequiredItems,
			distributionFilterItems,
			showItemsSlider,
		],
	);

	const updateStates = useCallback(
		(status: CheckboxState, newDist: Distribution) => {
			setFinalAvailableDist(newDist);
			setSelectedTagCheckboxMap(status);
			const { availableItems, checkboxStatus } =
				calculateCheckboxStatusAndAvailableItems(
					newDist,
					showItemsSlider,
					additionalParams?.studyRecommenedTopics,
				);
			const numRequiredItems = showItemsSlider
				? availableItems > 50
					? 20
					: getOptimalValue(availableItems, 10)
				: availableItems;

			setNumRequiredItems(numRequiredItems);
			setAvailableItems(availableItems);
			const requiredDist = showDistributionFilters
				? onCheckBoxStatusUpdated(
						newDist,
						checkboxStatus,
						numRequiredItems,
						distributionFilterItems,
					).requiredDistribution
				: newDist;

			setRequiredDistribution(requiredDist);
			setSelectedDistributionCheckboxMap(checkboxStatus);
		},
		[
			showDistributionFilters,
			distributionFilterItems,
			showItemsSlider,
			additionalParams?.studyRecommenedTopics,
		],
	);

	const handleTagCheckboxStates = useCallback(
		(status: CheckboxState) => {
			const { checkedTagIds, uncheckedTagIds = [] } = getTagIds(
				status,
				freeTagIds,
				hasAccess,
			);
			const finalTagIds = getFinalTagIds(checkedTagIds, uncheckedTagIds);
			const checkedTags = filterTagsByTagIds(allTags, finalTagIds);

			const types = checkedTags.map((tag) => tag.type);
			const isMixedType = new Set(types).size > 1 && crossTagSelection;
			const currentTagsDistForMode = getFinalTagsDistForMode(
				checkedTags,
				selectedLearningMode,
				showLearningMode,
			);

			const uniqTagDist = isItemArray(currentTagsDistForMode)
				? uniqBy(currentTagsDistForMode, "id")
				: uniqBy(currentTagsDistForMode, "id");

			const idSet = new Set();
			const intersectedTagsDist = currentTagsDistForMode.filter((value) => {
				if (idSet.has(value.id)) {
					return true;
				}
				idSet.add(value.id);
				return false;
			});

			const finalTagsDistForMode = isMixedType
				? intersectedTagsDist
				: uniqTagDist;
			const newDist = calculateTagDist(
				finalTagsDistForMode,
				distributionFilterItems,
				showPointsBasedScore,
			);
			updateStates(status, newDist);
		},
		[
			allTags,
			freeTagIds,
			hasAccess,
			selectedLearningMode,
			updateStates,
			showLearningMode,
			distributionFilterItems,
			showPointsBasedScore,
			crossTagSelection,
		],
	);

	useEffect(() => {
		const status: CheckboxState = {};
		if (isSessionCreationInProgress) {
			return;
		}

		if (selectedTagIds?.length) {
			for (const { id } of allTags) {
				status[id] = !!selectedTagIds.includes(id);
			}
		} else {
			for (const { id } of allTags) {
				status[id] = false;
			}
		}

		handleTagCheckboxStates(status);
	}, [
		allTags,
		handleTagCheckboxStates,
		selectedTagIds,
		isSessionCreationInProgress,
	]);

	const handleSetTagCheckboxMap = (selectedCheckboxId: string) => {
		const checkboxKey = Number(selectedCheckboxId);
		const newStatus = {
			...(selectedTagCheckboxMap || {}),
			[checkboxKey]: !selectedTagCheckboxMap?.[checkboxKey],
		};

		handleTagCheckboxStates(newStatus);
	};

	const handleSliderChange = useCallback(
		(value: number) => {
			const { requiredDistribution } = getNewDistributionCheckboxstatesAndCount(
				finalAvailableDist,
				selectedDistributionCheckboxMap,
				value,
				distributionFilterItems,
			);
			setRequiredDistribution(requiredDistribution);
			const { checkboxStatus } = calculateCheckboxStatusAndAvailableItems(
				requiredDistribution,
				showItemsSlider,
			);
			setSelectedDistributionCheckboxMap(checkboxStatus);
			setNumRequiredItems(value);
		},
		[
			finalAvailableDist,
			selectedDistributionCheckboxMap,
			distributionFilterItems,
			showItemsSlider,
		],
	);

	return {
		selectedLearningMode,
		handleSetLearningMode,
		selectedTagCheckboxMap,
		handleSetTagCheckboxMap,
		finalAvailableDist,
		requiredDistribution,
		numRequiredItems,
		selectedDistributionCheckboxMap,
		handleDistributionCheckboxState,
		availableItems,
		numTotalItemsFiltered,
		handleSliderChange,
	};
};

export default useSessionConfig;
